import { useMutation, useQueryClient } from 'react-query';

import type { IStandardErrorResponse } from 'src/api/common';
import type { IListing } from 'src/services/api/listings/types';
import { useNotifications } from 'src/notifications';
import { apiClient } from 'src/services/api/client';
import { getApiErrorMessage } from 'src/services/api/utils';
import { queryKeys } from 'src/services/api/constants';

type UpdateListingInferredTypeResponse = {
    listing: IListing;
};

type UpdateListingInferredTypePayload = {
    listingId: number;
    inferredType: 'fb:property:manual';
};

export const useUpdateListingInferredType = () => {
    const queryClient = useQueryClient();
    const { addNotification } = useNotifications();

    return useMutation<
        UpdateListingInferredTypeResponse,
        IStandardErrorResponse,
        UpdateListingInferredTypePayload
    >(async ({ listingId, inferredType }) => {
        const res = await apiClient(`api/landlord/listings/${listingId}/inferred-type`, {
            method: 'patch',
            json: { inferred_type: inferredType },
        }).json<UpdateListingInferredTypeResponse>();

        return res;
    }, {
        onSuccess: (data) => {
            queryClient.setQueryData<IListing>([queryKeys.listings, data.listing.id], data.listing);
            queryClient.invalidateQueries(queryKeys.listings);
            addNotification('Listing moved to regular', 'success');
        },
        onError: (error) => {
            const errorMessage = getApiErrorMessage(error) || 'Failed to update listing';
            addNotification(errorMessage, 'error');
        },
    });
};
